import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchClientJournal } from "../../store/thunks/clientJournalThunk";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import AppointmentSummary from "../../applicationUi/components/appointments/AppointmentSummary";
import { setSelectedAppointment } from "../../store/slices/appointmentsSlice";
import { format } from "date-fns";

function ClientJournal({ client }) {
  const dispatch = useDispatch();
  const [journal, setJournal] = useState(null);
  const [status, setStatus] = useState("idle");
  const [sortOrder, setSortOrder] = useState("Newest first");
  const [isAppointmentSummaryOpen, setIsAppointmentSummaryOpen] =
    useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  useEffect(() => {
    if (client._id) {
      setStatus("loading");
      dispatch(fetchClientJournal(client._id))
        .then((response) => {
          setJournal(response.payload);
          setStatus("succeeded");
        })
        .catch(() => setStatus("failed"));
    }
  }, [dispatch, client._id]);

  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setIsAppointmentSummaryOpen(true);
  };

  const handleCloseAppointmentSummary = () => {
    setSelectedAppointment(null);
    setIsAppointmentSummaryOpen(false);
  };

  const formatDate = (date) =>
    new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getAllEvents = () => {
    if (!journal) return [];

    const events = [];

    // Add appointments
    journal.appointments?.forEach((appointment) => {
      events.push({
        type: "appointment",
        data: appointment,
        date: new Date(appointment.start),
        title: appointment.title,
        status: appointment.status.join(", "),
        history: appointment.history,
        createdBy: appointment.createdBy
      });
    });

    // Add invoices
    journal.invoices?.forEach((invoice) => {
      events.push({
        type: "invoice",
        data: invoice,
        date: new Date(invoice.createdAt),
        title: `Invoice #${invoice.invoiceId}`,
        status: invoice.status,
        amount: invoice.amount.toFixed(2),
        createdBy: invoice.createdBy
      });
    });

    // Add client notes
    journal.clientNotes?.forEach((note) => {
      events.push({
        type: "note",
        data: note,
        date: new Date(note.createdAt),
        title: "Client Note",
        content: note.content,
        createdBy: note.createdBy
      });
    });

    // Add payments
    journal.payments?.forEach((payment) => {
      events.push({
        type: "payment",
        data: payment,
        date: new Date(payment.createdAt),
        title: "Payment",
        amount: payment.amount.toFixed(2),
        status: payment.status,
        createdBy: payment.createdBy
      });
    });

    // Add status history
    journal.statusHistory?.forEach((statusChange) => {
      events.push({
        type: "status_change",
        data: statusChange,
        date: new Date(statusChange.changedAt),
        title: "Status Changed",
        status: statusChange.status,
        comment: statusChange.comment,
        recallDate: statusChange.recallDate,
        createdBy: statusChange.changedBy
      });
    });

    // Sort events by date
    return events.sort((a, b) => {
      return sortOrder === "Newest first" ? b.date - a.date : a.date - b.date;
    });
  };

  const getEventIcon = (type) => {
    switch (type) {
      case "appointment":
        return (
          <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center ring-4 ring-white shadow-lg">
            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        );
      case "invoice":
        return (
          <div className="h-10 w-10 rounded-full bg-green-600 flex items-center justify-center ring-4 ring-white shadow-lg">
            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
      case "note":
        return (
          <div className="h-10 w-10 rounded-full bg-purple-600 flex items-center justify-center ring-4 ring-white shadow-lg">
            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
        );
      case "payment":
        return (
          <div className="h-10 w-10 rounded-full bg-emerald-600 flex items-center justify-center ring-4 ring-white shadow-lg">
            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
        );
      case "status_change":
        return (
          <div className="h-10 w-10 rounded-full bg-orange-600 flex items-center justify-center ring-4 ring-white shadow-lg">
            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="h-10 w-10 rounded-full bg-gray-500 flex items-center justify-center ring-4 ring-white shadow-lg">
            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case "appointment":
        return "text-blue-800 bg-blue-50 border border-blue-200";
      case "invoice":
        return "text-green-800 bg-green-50 border border-green-200";
      case "note":
        return "text-purple-800 bg-purple-50 border border-purple-200";
      case "payment":
        return "text-emerald-800 bg-emerald-50 border border-emerald-200";
      case "status_change":
        return "text-orange-800 bg-orange-50 border border-orange-200";
      default:
        return "text-gray-800 bg-gray-50 border border-gray-200";
    }
  };

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-sm bg-gray-200 h-12 w-12"></div>
          <div className="flex-1 space-y-2 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (status === "failed" || !journal) {
    return (
      <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-sm bg-gray-100">
            <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No journal data</h3>
          <p className="mt-1 text-sm text-gray-500">No journal data available for this client.</p>
        </div>
      </div>
    );
  }

  const events = getAllEvents();
  let currentDate = null;

  return (
    <div className="bg-white rounded-sm shadow-sm border border-gray-200 p-6">
      <div className="flex flex-row justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
            Client Journal
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Timeline of events for {client.firstName} {client.lastName}
          </p>
        </div>

        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-900 mr-3">
            Sort by
          </span>
          <select
            id="sortOrder"
            name="sortOrder"
            value={sortOrder}
            onChange={handleSortChange}
            className="rounded-sm border border-gray-300 py-2 pl-3 pr-8 text-sm text-gray-900 focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white shadow-sm">
            <option>Oldest first</option>
            <option>Newest first</option>
          </select>
        </div>
      </div>

      <div className="flow-root">
        <ul role="list" className="-mb-8">
          {events.map((event, eventIdx) => {
            const isNewDay =
              !currentDate ||
              format(currentDate, "yyyy-MM-dd") !==
                format(event.date, "yyyy-MM-dd");

            if (isNewDay) {
              currentDate = event.date;
            }

            return (
              <li key={`${event.type}-${event.data._id}-${eventIdx}`}>
                {isNewDay && (
                  <div className="sticky top-0 z-10 flex items-center gap-x-4 bg-white py-4 border-b border-gray-100 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-gray-900 flex items-center justify-center">
                        <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="text-base font-semibold text-gray-900">
                        {format(event.date, "EEEE, MMMM d, yyyy")}
                      </div>
                    </div>
                  </div>
                )}
                <div className="relative pb-8">
                  {eventIdx !== events.length - 1 && (
                    <span
                      className="absolute left-5 top-12 -ml-px h-full w-0.5 bg-gray-300"
                      aria-hidden="true"
                    />
                  )}
                  <div className="relative flex items-start space-x-4">
                    <div className="relative z-10">
                      {getEventIcon(event.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="bg-white rounded-sm border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getEventColor(
                                  event.type
                                )}`}>
                                {event.title}
                              </span>
                              <time
                                dateTime={event.date.toISOString()}
                                className="text-sm text-gray-500 font-medium">
                                {format(event.date, "h:mm a")}
                              </time>
                            </div>
                            
                            <div className="space-y-2">
                              {event.type === "appointment" && (
                                <div className="text-sm text-gray-700">
                                  <span className="font-medium">Status:</span> {event.status}
                                </div>
                              )}
                              {(event.type === "invoice" || event.type === "payment") && (
                                <div className="text-sm text-gray-700">
                                  <span className="font-medium">Amount:</span> 
                                  <span className="text-lg font-semibold text-green-600 ml-1">
                                    ${event.amount}
                                  </span>
                                </div>
                              )}
                              {event.type === "note" && (
                                <div className="text-sm text-gray-700 bg-gray-50 rounded-sm p-3 border-l-4 border-purple-400">
                                  <p className="italic">"{event.content}"</p>
                                </div>
                              )}
                              {event.type === "invoice" && (
                                <div className="text-sm text-gray-600">
                                  <span className="font-medium">Status:</span> 
                                  <span className={`ml-1 px-2 py-0.5 rounded-sm text-xs font-medium ${
                                    event.status === 'paid' ? 'bg-green-100 text-green-800' :
                                    event.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {event.status}
                                  </span>
                                </div>
                              )}
                              {event.type === "payment" && (
                                <div className="text-sm text-gray-600">
                                  <span className="font-medium">Status:</span> 
                                  <span className={`ml-1 px-2 py-0.5 rounded-sm text-xs font-medium ${
                                    event.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    event.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {event.status}
                                  </span>
                                </div>
                              )}
                              {event.type === "status_change" && (
                                <div className="space-y-2">
                                  <div className="text-sm text-gray-700">
                                    <span className="font-medium">New Status:</span>
                                    <span className={`ml-2 px-2 py-1 rounded-sm text-xs font-medium capitalize ${
                                      event.status === 'active' ? 'bg-green-100 text-green-800' :
                                      event.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                                      event.status === 'discharged' ? 'bg-blue-100 text-blue-800' :
                                      event.status === 'recall' ? 'bg-orange-100 text-orange-800' :
                                      event.status === 'deceased' ? 'bg-red-100 text-red-800' :
                                      event.status === 'other' ? 'bg-purple-100 text-purple-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {event.status}
                                    </span>
                                  </div>
                                  {event.recallDate && (
                                    <div className="text-sm text-gray-700">
                                      <span className="font-medium">Recall Date:</span>
                                      <span className="ml-1 text-orange-700 font-medium">
                                        {format(new Date(event.recallDate), "MMM d, yyyy")}
                                      </span>
                                    </div>
                                  )}
                                  {event.comment && (
                                    <div className="text-sm text-gray-700 bg-orange-50 rounded-sm p-3 border-l-4 border-orange-400">
                                      <span className="font-medium text-orange-800">Comment:</span>
                                      <p className="italic mt-1 text-orange-900">"{event.comment}"</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {event.createdBy && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center text-xs text-gray-500">
                              <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              Created by {event.createdBy.firstName} {event.createdBy.lastName}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {events.length === 0 && (
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100">
            <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No events yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Events will appear here as they are created for this client.
          </p>
        </div>
      )}

      {/* Appointment Summary Modal */}
      {selectedAppointment && (
        <AppointmentSummary
          open={isAppointmentSummaryOpen}
          onClose={handleCloseAppointmentSummary}
          appointment={selectedAppointment}
          onReschedule={(appt) => console.log("Reschedule:", appt)}
        />
      )}
    </div>
  );
}

export default ClientJournal;
